import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkle, Ghost, CurrencyDollar, ArrowRight, CheckCircle, XCircle, Play,
} from '@phosphor-icons/react';

/* /ai-agents — landing page positioning SalesCrunch's built-in agents
   against DIY agent-builder platforms (Twin.so et al). */

const VALUE_PROPS = [
  {
    icon: <Sparkle size={22} weight="fill" className="text-[#F39C12]" />,
    title: 'Pre-Call Intelligence Briefs',
    tag: 'Built-in',
    body: 'Never walk into a meeting cold. SalesCrunch automatically synthesizes past notes, deal activity, and history into a 30-second bulleted summary right on your contact screen.',
    testid: 'agents-prop-brief',
  },
  {
    icon: <Ghost size={22} weight="fill" className="text-[#F39C12]" />,
    title: 'GhostBuster AI',
    tag: 'Built-in',
    body: 'No lead gets left behind. Our proactive AI flags silent deals with no touchpoints in 14 days and drafts a personalized follow-up from your own notes — one click to your composer.',
    testid: 'agents-prop-ghostbuster',
  },
  {
    icon: <CurrencyDollar size={22} weight="fill" className="text-[#F39C12]" />,
    title: 'Transparent $30/User Rate',
    tag: 'No token math',
    body: 'No credit surprises. 150 AI Concierge queries included every month, with simple one-time top-ups when you need more. Your whole CRM and the agents, one price.',
    testid: 'agents-prop-price',
  },
];

const COMPARE = [
  ['Setup', 'Prompt engineering, webhooks, API wiring, testing', 'Zero. Agents work the moment you sign in'],
  ['Who it\u2019s for', 'Developers, agencies, automation builders', 'Business owners and sales reps'],
  ['Pricing', 'Credit & token consumption — varies per task run', '$30/user/mo, 150 AI queries included'],
  ['The CRM itself', 'Bring your own (HubSpot / Salesforce extra)', 'Included — contacts, notes, pipeline, calendar, email'],
  ['Maintenance', 'Agents break when your prompts or APIs drift', 'We maintain the agents. You sell'],
];

const VIDEOS = [
  { src: '/videocrunch-precall-brief.mp4', poster: '/videocrunch-poster-precall-brief.jpg', title: 'Pre-Call Brief — 60 seconds' },
  { src: '/videocrunch-ghostbuster.mp4', poster: '/videocrunch-poster-ghostbuster.jpg', title: 'GhostBuster — 55 seconds' },
];

export default function AIAgentsPage() {
  useEffect(() => {
    document.title = 'Built-in AI Sales Agents — SalesCrunchCRM';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="ai-agents-page">
      {/* Nav */}
      <nav className="border-b border-slate-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-black text-[#0B5394]" data-testid="agents-nav-home">SalesCrunch<span className="text-[#F39C12]">CRM</span></Link>
          <Link to="/" className="text-sm font-bold text-white bg-[#0B5394] hover:bg-[#073763] rounded-md px-4 py-2 transition-colors" data-testid="agents-nav-cta">
            Start free trial
          </Link>
        </div>
      </nav>

      {/* Hero — Angle 1: Zero Setup attack */}
      <header className="px-6 pt-16 pb-14 border-b border-slate-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-[10px] uppercase tracking-widest font-black text-[#F39C12] mb-4">
            Autonomous AI agents — built in, not bolted on
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] max-w-3xl mb-5">
            Stop building AI agents.<br />Start closing deals.
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed mb-8">
            While other tools force you to engineer custom prompts and connect
            complex webhooks, SalesCrunchCRM comes pre-configured with a
            built-in AI Concierge. Instant Pre-Call Briefings and automated
            GhostBuster re-engagements, right out of the box — no setup required.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/" className="inline-flex items-center gap-2 bg-[#F39C12] hover:bg-[#d9880d] text-black font-black rounded-md px-6 py-3 transition-colors" data-testid="agents-hero-cta">
              Start free trial — $30/user/mo <ArrowRight size={16} weight="bold" />
            </Link>
            <a href="#agent-demos" className="inline-flex items-center gap-2 text-sm font-bold text-[#0B5394] hover:underline" data-testid="agents-hero-demo-link">
              <Play size={14} weight="fill" /> See the 60-second demos
            </a>
          </div>
          <p className="text-xs text-slate-400 mt-3">14-day free trial · $0 today · cancel anytime</p>
        </div>
      </header>

      {/* 3 value props */}
      <section className="px-6 py-14 bg-slate-50 border-b border-slate-100">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-5">
          {VALUE_PROPS.map((p) => (
            <div key={p.title} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid={p.testid}>
              <div className="flex items-center justify-between mb-3">
                {p.icon}
                <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">{p.tag}</span>
              </div>
              <h3 className="font-black text-base mb-2">{p.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison — Angle 3 */}
      <section className="px-6 py-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg md:text-xl font-black text-center mb-2">
            All the power of an autonomous AI agent. Zero prompt engineering.
          </h2>
          <p className="text-sm text-slate-500 text-center max-w-2xl mx-auto mb-8">
            Why pay $100+/month for complex agent builders when native Pre-Call
            Intel and automated lead recovery come built into a $30/user/mo sales CRM?
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border border-slate-200 rounded-lg overflow-hidden text-sm" data-testid="agents-compare-table">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th className="text-left px-4 py-3 font-black w-1/5"></th>
                  <th className="text-left px-4 py-3 font-black w-2/5">DIY agent platforms</th>
                  <th className="text-left px-4 py-3 font-black w-2/5 bg-[#0B5394]">SalesCrunchCRM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {COMPARE.map(([label, them, us]) => (
                  <tr key={label}>
                    <td className="px-4 py-3 font-bold text-slate-500 align-top">{label}</td>
                    <td className="px-4 py-3 text-slate-600 align-top">
                      <span className="inline-flex items-start gap-1.5"><XCircle size={15} weight="fill" className="text-red-400 mt-0.5 shrink-0" /> {them}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-800 align-top bg-blue-50/40">
                      <span className="inline-flex items-start gap-1.5"><CheckCircle size={15} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" /> {us}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Demo videos */}
      <section id="agent-demos" className="px-6 py-16 bg-slate-900 text-white scroll-mt-10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg md:text-xl font-black text-center mb-2">Watch the agents work</h2>
          <p className="text-sm text-white/60 text-center mb-8">
            Real product, real clicks — both videos were produced automatically by VideoCrunch.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {VIDEOS.map((v) => (
              <div key={v.src} className="rounded-lg overflow-hidden border border-white/10" data-testid={`agents-video-${v.src.includes('ghost') ? 'ghostbuster' : 'brief'}`}>
                <video controls preload="none" poster={v.poster} className="w-full aspect-video bg-black">
                  <source src={v.src} type="video/mp4" />
                </video>
                <div className="px-4 py-3 text-sm font-bold bg-white/5">{v.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA — Angle 2 */}
      <section className="px-6 py-16 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black mb-3">
            The first CRM that works <span className="text-[#0B5394]">for</span> you — not the other way around.
          </h2>
          <p className="text-sm text-slate-600 mb-7">
            Legacy-fast contact management plus proactive AI that briefs you
            before calls and catches silent leads before they go cold.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 bg-[#0B5394] hover:bg-[#073763] text-white font-black rounded-md px-7 py-3.5 transition-colors" data-testid="agents-footer-cta">
            Claim your AI-powered workspace <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <footer className="px-6 py-8 border-t border-slate-100 text-center text-xs text-slate-400">
        SalesCrunchCRM — $30/user/month · <Link to="/affiliates" className="text-[#0B5394] hover:underline">Partner program: 40% recurring</Link>
      </footer>
    </div>
  );
}
